import { DedicatedServerCreate, DedicatedServerCreateOptions } from '../../../api/bareMetalTypes'
import { PreorderCreate, PreorderCreateOptions } from '../../../api/dedicatedPreorderTypes'

export type CreationMode = 'stock' | 'preorder'
export type CreationOptions = DedicatedServerCreateOptions | PreorderCreateOptions
export type CreationDraft = {
  name: string
  hostname: string
  server_id: string
  cpu_id: string
  cpu_count: number
  ram_id: string
  ram_count: number
  disk_ids: string[]
  os: string
  password: string
  ssh_key_ids: string[]
  comment: string
}
export const emptyDraft = (): CreationDraft => ({
  name: '', hostname: '', server_id: '', cpu_id: '', cpu_count: 1,
  ram_id: '', ram_count: 1, disk_ids: [''], os: '', password: '', ssh_key_ids: [], comment: '',
})

export const validateConfiguration = (values: CreationDraft, mode: CreationMode, options: CreationOptions, required: string, maxLength: string, integer: string) => {
  const errors: Record<string, string> = {}
  if (!values.hostname.trim()) errors.hostname = required
  if (values.hostname.length > 255) errors.hostname = maxLength
  if (values.name.length > 255) errors.name = maxLength
  if (!options.os.some(os => String(os.id) === values.os)) errors.os = required
  if (mode === 'stock' && 'servers' in options) {
    if (!options.servers.some(server => server.id === values.server_id)) errors.server_id = required
  } else if ('cpus' in options) {
    if (!options.cpus.some(cpu => cpu.id === values.cpu_id)) errors.cpu_id = required
    if (!options.rams.some(ram => ram.id === values.ram_id)) errors.ram_id = required
    if (!Number.isSafeInteger(values.cpu_count) || values.cpu_count < 1) errors.cpu_count = integer
    if (!Number.isSafeInteger(values.ram_count) || values.ram_count < 1) errors.ram_count = integer
    if (!values.disk_ids.length || values.disk_ids.some(id => !options.disks.some(disk => disk.id === id))) errors.disk_ids = required
  }
  return errors
}

const installationPayload = (values: CreationDraft) => ({
  hostname: values.hostname.trim(), os: Number(values.os), password: values.password,
})
const selectedKeys = (values: CreationDraft, options: CreationOptions) =>
  options.os.find(os => String(os.id) === values.os)?.allow_ssh_keys
    ? values.ssh_key_ids.filter(Boolean) : []

export const serverPayload = (values: CreationDraft, options: CreationOptions): DedicatedServerCreate => ({
  ...installationPayload(values), server_id: values.server_id, ssh_keys: selectedKeys(values, options),
})
export const preorderPayload = (values: CreationDraft, options: CreationOptions): PreorderCreate => ({
  ...installationPayload(values),
  ...(values.name.trim() ? { name: values.name.trim() } : {}),
  cpu_id: values.cpu_id, cpu_count: values.cpu_count,
  ram_id: values.ram_id, ram_count: values.ram_count,
  disk_ids: values.disk_ids, ssh_key_ids: selectedKeys(values, options), comment: values.comment.trim(),
})

export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  const messages = (value: unknown): string[] => {
    if (typeof value === 'string') return [value]
    if (Array.isArray(value)) return value.flatMap(messages)
    if (value && typeof value === 'object') return Object.entries(value).flatMap(([key, item]) =>
      messages(item).map(message => ['detail', 'non_field_errors'].includes(key) ? message : `${key}: ${message}`))
    return []
  }
  return data && typeof data === 'object' ? messages(data).join('\n') || fallback : fallback
}
